import React from "react";

export default function IntonationFeedback({ assessment }) {
  const { 
    DTW, 
    correlation, 
    slope_diff,
    intensity_DTW,
    intensity_correlation,
    intensity_slope_diff 
  } = assessment;

  // Check if intensity data is available
  const hasIntensity = intensity_DTW !== undefined && intensity_correlation !== undefined;

  // ===============================
  // Check for microphone/recording issues
  // ===============================
  const hasRecordingIssue = DTW >= 9998 || (hasIntensity && intensity_DTW >= 9998);
  
  // ===============================
  // If recording issue, show only warning and return early
  // ===============================
  if (hasRecordingIssue) {
    return (
      <div className="flex flex-col border-2 p-4 rounded-md shadow-sm space-y-3 w-full max-w-full">
        {/* Warning Banner for Recording Issues */}
        <div className="bg-orange-100 border border-orange-400 text-orange-800 px-4 py-3 rounded-md">
          <div className="flex items-center">
            <span className="text-lg mr-2">⚠️</span>
            <div>
              <strong className="block text-lg">Recording Issue Detected</strong>
              <span className="text-sm block mt-1">
                This usually means your microphone was off, the recording was too quiet, or no speech was detected. 
              </span>
              <span className="text-sm block mt-2">
                <strong>Please check:</strong>
                <ul className="list-disc list-inside mt-1 ml-2">
                  <li>Microphone permissions are enabled</li>
                  <li>You're speaking clearly and loudly enough</li>
                  <li>There's no background noise interfering</li>
                </ul>
              </span>
              <span className="text-sm block mt-2">
                Try recording again with better audio conditions.
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===============================
  // Normal assessment logic (only executed when no recording issues)
  // ===============================

  // DTW feedback thresholds adjusted for trimmed sequences
  let dtwFeedback = "";
  let dtwColor = "";
  if (DTW < 0.18) {
    dtwFeedback = "Excellent match";
    dtwColor = "text-green-600";
  } else if (DTW < 0.40) {
    dtwFeedback = "Good";
    dtwColor = "text-yellow-600";
  } else if (DTW < 0.55) {
    dtwFeedback = "Needs work";
    dtwColor = "text-orange-600";
  } else {
    dtwFeedback = "Bad";
    dtwColor = "text-red-600";
  }

  // Intensity DTW feedback
  let intensityDtwFeedback = "";
  let intensityDtwColor = "";
  if (hasIntensity) {
    if (intensity_DTW < 0.15) {
      intensityDtwFeedback = "Excellent loudness match";
      intensityDtwColor = "text-green-600";
    } else if (intensity_DTW < 0.35) {
      intensityDtwFeedback = "Good loudness pattern";
      intensityDtwColor = "text-yellow-600";
    } else if (intensity_DTW < 0.50) {
      intensityDtwFeedback = "Loudness needs work";
      intensityDtwColor = "text-orange-600";
    } else {
      intensityDtwFeedback = "Loudness pattern incorrect";
      intensityDtwColor = "text-red-600";
    }
  }

  // Slope/correlation thresholds
  let slopeFeedback = "";
  let slopeColor = "";
  if (correlation < 0) {
    slopeFeedback = "Opposite intonation direction";
    slopeColor = "text-red-600";
  } else if (slope_diff < 0.08) {
    slopeFeedback = "Global pitch trend correct";
    slopeColor = "text-green-600";
  } else if (slope_diff < 0.18) {
    slopeFeedback = "Minor trend mismatch";
    slopeColor = "text-yellow-600";
  } else {
    slopeFeedback = "Global pitch trend incorrect";
    slopeColor = "text-red-600";
  }

  // Intensity correlation feedback
  let intensitySlopeFeedback = "";
  let intensitySlopeColor = "";
  if (hasIntensity) {
    if (intensity_correlation < 0) {
      intensitySlopeFeedback = "Opposite loudness pattern";
      intensitySlopeColor = "text-red-600";
    } else if (intensity_slope_diff < 0.06) {
      intensitySlopeFeedback = "Loudness trend correct";
      intensitySlopeColor = "text-green-600";
    } else if (intensity_slope_diff < 0.15) {
      intensitySlopeFeedback = "Minor loudness trend mismatch";
      intensitySlopeColor = "text-yellow-600";
    } else {
      intensitySlopeFeedback = "Loudness trend incorrect";
      intensitySlopeColor = "text-red-600";
    }
  }

  // Combined suggestions
  let suggestion = "";
  let suggestionColor = "";

  if (correlation < 0) {
    suggestion = "Check pitch direction: it is opposite of the reference.";
    suggestionColor = "bg-red-100 text-red-800";
  } else if (hasIntensity && intensity_correlation < 0) {
    suggestion = "Check loudness pattern: it moves opposite to the reference.";
    suggestionColor = "bg-red-100 text-red-800";
  } else if (DTW < 0.18 && slope_diff < 0.08 && 
             (!hasIntensity || (intensity_DTW < 0.15 && intensity_slope_diff < 0.06))) {
    suggestion = "Native-like intonation and loudness! Excellent job.";
    suggestionColor = "bg-green-100 text-green-800";
  } else if (DTW < 0.40 && slope_diff < 0.18 && 
             (!hasIntensity || (intensity_DTW < 0.35 && intensity_slope_diff < 0.15))) {
    suggestion = "Good intonation and loudness. Minor improvements needed.";
    suggestionColor = "bg-yellow-100 text-yellow-800";
  } else if (DTW >= 0.55 || (hasIntensity && intensity_DTW >= 0.50)) {
    suggestion = "Focus on both pitch contour and loudness pattern. Practice matching the reference more closely.";
    suggestionColor = "bg-red-100 text-red-800";
  } else if (slope_diff >= 0.18 || (hasIntensity && intensity_slope_diff >= 0.15)) {
    suggestion = "Work on the overall trend - both pitch and loudness should follow similar patterns as the reference.";
    suggestionColor = "bg-orange-100 text-orange-800";
  } else {
    suggestion = "Intonation needs practice. Focus on pitch contour and final slope.";
    suggestionColor = "bg-red-100 text-red-800";
  }

  // ===============================
  // Render normal assessment (only when no recording issues)
  // ===============================
  return (
    <div className="flex flex-col border-2 p-4 rounded-md shadow-sm space-y-3 w-full max-w-full">
      {/* Pitch DTW */}
      <div className="break-words">
        <strong>Pitch DTW (normalized):</strong>{" "}
        <span className={dtwColor}>
          {DTW.toFixed(3)} — {dtwFeedback}
        </span>
      </div>

      {/* Intensity DTW */}
      {hasIntensity && (
        <div className="break-words">
          <strong>Loudness DTW (normalized):</strong>{" "}
          <span className={intensityDtwColor}>
            {intensity_DTW.toFixed(3)} — {intensityDtwFeedback}
          </span>
        </div>
      )}

      {/* Pitch Correlation */}
      <div className="break-words">
        <strong>Pitch Correlation:</strong> {correlation.toFixed(2)}
      </div>

      {/* Intensity Correlation */}
      {hasIntensity && (
        <div className="break-words">
          <strong>Loudness Correlation:</strong> {intensity_correlation.toFixed(2)}
        </div>
      )}

      {/* Pitch Slope Diff */}
      <div className="break-words">
        <strong>Pitch Slope difference:</strong>{" "}
        <span className={slopeColor}>
          {slope_diff.toFixed(3)} — {slopeFeedback}
        </span>
      </div>

      {/* Intensity Slope Diff */}
      {hasIntensity && (
        <div className="break-words">
          <strong>Loudness Slope difference:</strong>{" "}
          <span className={intensitySlopeColor}>
            {intensity_slope_diff.toFixed(3)} — {intensitySlopeFeedback}
          </span>
        </div>
      )}

      {/* Suggestion */}
      <div className={`p-2 rounded max-w-full w-full break-words ${suggestionColor}`}>
        <strong className="block mb-1">Suggestion:</strong>
        <span className="block whitespace-normal break-words">
          {suggestion}
        </span>
      </div>

      {/* Logic */}
      <div className="text-sm text-gray-700 break-words">
  <strong className="block mb-2">Logic behind suggestion:</strong>
  <ul className="list-disc list-inside space-y-2">
    <li className={correlation < 0 ? "text-red-600 font-medium" : ""}>
      <strong>If correlation &lt; 0:</strong> Check pitch direction — it is opposite of the reference.
    </li>
    
    {hasIntensity && (
      <li className={intensity_correlation < 0 ? "text-red-600 font-medium" : ""}>
        <strong>If loudness correlation &lt; 0:</strong> Check loudness pattern — it moves opposite to reference.
      </li>
    )}
    
    <li className={
      DTW < 0.18 && slope_diff < 0.08 && 
      (!hasIntensity || (intensity_DTW < 0.15 && intensity_slope_diff < 0.06)) 
        ? "text-green-600 font-medium" 
        : ""
    }>
      <strong>Excellent if:</strong> Pitch DTW &lt; 0.18 and pitch slope_diff &lt; 0.08
      {hasIntensity && " and loudness DTW &lt; 0.15 and loudness slope_diff &lt; 0.06"}
      {" "}— Native-like intonation and loudness!
    </li>
    
    <li className={
      DTW < 0.40 && slope_diff < 0.18 && 
      (!hasIntensity || (intensity_DTW < 0.35 && intensity_slope_diff < 0.15)) 
        ? "text-yellow-600 font-medium" 
        : ""
    }>
      <strong>Good if:</strong> Pitch DTW &lt; 0.40 and pitch slope_diff &lt; 0.18
      {hasIntensity && " and loudness DTW &lt; 0.35 and loudness slope_diff &lt; 0.15"}
      {" "}— Good intonation and loudness.
    </li>
    
    <li className={DTW >= 0.55 || (hasIntensity && intensity_DTW >= 0.50) ? "text-red-600 font-medium" : ""}>
      <strong>If poor DTW:</strong> Focus on both pitch contour and loudness pattern.
    </li>
    
    <li className={slope_diff >= 0.18 || (hasIntensity && intensity_slope_diff >= 0.15) ? "text-orange-600 font-medium" : ""}>
      <strong>If poor slope match:</strong> Work on overall trend of pitch and loudness.
    </li>
  </ul>
</div>
    </div>
  );
}